import React, { useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { useViewport, OverlayProvider } from "@bca/ui-library";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import HomeScreen from "./HomeScreen/HomeScreen";
import CompanyInformation from "./CompanyInformation/CompanyInformation";
import CompanyDetails from "./CompanyDetails/CompanyDetails";
import ContactInformation from "./ContactInformation/ContactInformation";
import FinalDetails from "./FinalDetails/FinalDetails";
import ThankYou from "./ThankYou/ThankYou";
import {
  Page,
  StyledContainer,
  StyledTypography,
  StyledA,
} from "./index.styled";
import AddContactPerson from "./AddContactPerson/AddContactPerson";
import LanguageModal from "../components/LanguageModal/LanguageModal";
import { getHeaderAndFooter } from "../reducers/headerAndFooterSlice";
import Footer from "../components/Footer/Footer";
import Header from "../components/Header/Header";
import Menu from "../components/Menu/Menu";
import { version } from "../../package.json";
import {
  ADD_CONTACT_PERSON_PATH,
  COMPANY_DETAILS_PATH,
  COMPANY_INFORMATION_PATH,
  CONTACT_INFORMATION_PATH,
  FINAL_DETAILS_PATH,
  HOME_PATH,
  THANK_YOU_PATH,
  VERSION_PATH,
} from "../constants/paths";
import LoadingScreen from "../components/LoadingScreen/LoadingScreen";

let router;
if (process.env.REACT_APP_ENV === "PROD") {
  router = createBrowserRouter([
    {
      path: HOME_PATH,
      element: <HomeScreen />,
    },
    {
      path: COMPANY_INFORMATION_PATH,
      element: <CompanyInformation />,
    },
    {
      path: COMPANY_DETAILS_PATH,
      element: <CompanyDetails />,
    },
    {
      path: CONTACT_INFORMATION_PATH,
      element: <ContactInformation />,
    },
    {
      path: FINAL_DETAILS_PATH,
      element: <FinalDetails />,
    },
    {
      path: THANK_YOU_PATH,
      element: <ThankYou />,
    },
    {
      path: ADD_CONTACT_PERSON_PATH,
      element: <AddContactPerson />,
    },
  ]);
} else {
  router = createBrowserRouter([
    {
      path: HOME_PATH,
      element: <HomeScreen />,
    },
    {
      path: COMPANY_INFORMATION_PATH,
      element: <CompanyInformation />,
    },
    {
      path: COMPANY_DETAILS_PATH,
      element: <CompanyDetails />,
    },
    {
      path: CONTACT_INFORMATION_PATH,
      element: <ContactInformation />,
    },
    {
      path: FINAL_DETAILS_PATH,
      element: <FinalDetails />,
    },
    {
      path: THANK_YOU_PATH,
      element: <ThankYou />,
    },
    {
      path: ADD_CONTACT_PERSON_PATH,
      element: <AddContactPerson />,
    },
    {
      path: VERSION_PATH,
      element: (
        <div>
          This project is running on version <b>{version}</b>
        </div>
      ),
    },
  ]);
}

const Pages = () => {
  const [isMobileMenuOpened, setIsMobileMenuOpened] = React.useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = React.useState(false);

  const { page_content } = useSelector((state) => state.headerAndFooter);

  const { showLoadingScreen } = useSelector((state) => state.loadingScreen);
  const { showServerErrorMessage } = useSelector(
    (state) => state.errorManagement
  );

  const vp = useViewport();
  const isMobile = vp.lessThan.xl;
  const { country_name: countrySelected } = useSelector((state) => state.form);

  const { value: languageCode } = useSelector((state) => state.languageOptions);
  const handleSaveLanguage = () => {
    setIsLanguageModalOpen(false);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getHeaderAndFooter(countrySelected, languageCode));
  }, [languageCode, dispatch, countrySelected]);

  return (
    <>
      <Helmet>
        <title>{page_content?.browser_title}</title>
      </Helmet>
      <OverlayProvider>
        {isMobile && isMobileMenuOpened ? (
          <Menu
            onCloseClick={() => setIsMobileMenuOpened(false)}
            headerHomeText={page_content?.header_home_text}
            headerHomeUrl={page_content?.header_home_url}
            menuHomeText={page_content?.menu_text}
          />
        ) : (
          <Page>
            {isLanguageModalOpen && (
              <LanguageModal
                isMobile={isMobile}
                handleModal={() => setIsLanguageModalOpen(false)}
                handleSaveLanguage={handleSaveLanguage}
              />
            )}
            <Header
              isMobile={isMobile}
              language={languageCode}
              countrySelected={countrySelected}
              headerBcaText={page_content?.header_bca_text}
              headerBcaUrl={page_content?.header_bca_url}
              onMenuClick={() => setIsMobileMenuOpened(true)}
              onLanguageClick={() => setIsLanguageModalOpen(true)}
            />
            {showLoadingScreen && <LoadingScreen />}
            <StyledContainer
              isMobile={isMobile}
              style={{ display: showLoadingScreen ? "none" : "initial" }}
            >
              {showServerErrorMessage ? (
                <StyledTypography>
                  Internal server error. We apologise for the inconvenience, we
                  are investigating. Please check back in a while. Back to
                  &nbsp;
                  <StyledA href="https://www.bca.com">
                    https://www.bca.com.
                  </StyledA>
                </StyledTypography>
              ) : (
                <RouterProvider router={router} />
              )}
            </StyledContainer>
            <Footer
              isMobile={isMobile}
              countrySelected={countrySelected}
              footerText={page_content?.footer_text}
            />
          </Page>
        )}
      </OverlayProvider>
    </>
  );
};

export default Pages;
